import { onBeforeUnmount } from "vue";
import Shepherd from "shepherd.js";
import type { Placement } from "@popperjs/core/lib/enums";
import { delayMs } from "@/utils";

const padding = 14;
const stepDefaults = {
  scrollTo: true,
  canClickTarget: false,
  cancelIcon: { enabled: false },
  popperOptions: {
    modifiers: [{ name: "offset", options: { offset: [0, padding + 7] } }],
  },
  modalOverlayOpeningPadding: padding,
  modalOverlayOpeningRadius: 16,
};
const tourDefaults = {
  useModalOverlay: true,
};

export const useShepherdTour = (opts?: Partial<Shepherd.Tour.TourOptions>) => {
  // Combine all the options
  const { defaultStepOptions, ...tourOpts } = opts ?? {};
  const stepOptsFinal = Object.assign(
    {},
    stepDefaults,
    defaultStepOptions ?? {}
  );
  const tourOptsFinal = Object.assign(
    {},
    tourDefaults,
    {
      defaultStepOptions: stepOptsFinal,
    },
    tourOpts ?? {}
  );
  const tour = new Shepherd.Tour(tourOptsFinal);

  const next = {
    text: "next",
    action: tour.next,
    classes: "button-next",
  };
  const back = {
    text: "back",
    action: tour.back,
    classes: "button-back",
  };
  const cancel = {
    text: "cancel",
    action: () => {
      // Make sure that step event is also fired
      tour.getCurrentStep()?.cancel();
      tour.cancel();
    },
    classes: "button-cancel",
  };
  const finish = {
    text: "finish",
    action: () => {
      // Make sure that step event is also fired
      tour.getCurrentStep()?.complete();
      tour.complete();
    },
    classes: "button-finish",
  };
  const startButtons = [cancel, next];
  const midButtons = [cancel, back, next];
  const endButtons = [back, finish];

  onBeforeUnmount(tour.cancel);

  return {
    tour,
    startButtons,
    midButtons,
    endButtons,
  };
};

// Simplified Shepherd.Step.StepOptions
export type TourStep = Omit<
  Shepherd.Step.StepOptions,
  "attachTo" | "buttons"
> & {
  element?:
    | HTMLElement
    | string
    | (() => HTMLElement | string | null | undefined);
  on?: Placement;
  // Constructor/destructor pair that will added to appropriate entry/leave events.
  onEnterLeave?: {
    onEnter: () => unknown | Promise<unknown>;
    onLeave: () => void;
  };
};

export const useShepherdTourSteps = (
  steps: TourStep[],
  opts?: Partial<Shepherd.Tour.TourOptions>
) => {
  const { tour, ...other } = useShepherdTour(opts);

  steps.forEach((step, i) => {
    const { element, on, onEnterLeave, ...rest } = step;

    // Add progress indicator
    if (rest.when == undefined) rest.when = {};
    if (rest.when.show != undefined)
      throw Error("Overriding 'show' not allowed");
    rest.when.show = () => {
      const elem = Shepherd.activeTour?.getCurrentStep()?.getElement();
      const footer = elem?.querySelector(".shepherd-footer");

      if (footer != null) {
        const progress = document.createElement("progress");
        progress.classList.add("progress", "w-full", "mr-2", "bg-base-200");
        progress.value =
          steps.length <= 1 ? 0 : Math.floor((i / (steps.length - 1)) * 100);
        progress.max = 100;
        footer.insertBefore(progress, footer.querySelector("button"));
      }
    };

    // Add callbacks to all entry/leave points of the step. This assumes that
    // cancel/complete buttons will first call according step methods.
    // We also use beforeShowPromise so that there is at least one event loop
    // step before shepherd tries to position popper window - this way we can
    // show a hidden element and have the popup positioned correctly.
    if (onEnterLeave != undefined) {
      const events = [
        rest.when?.hide,
        rest.when?.cancel,
        rest.when?.complete,
        rest.beforeShowPromise,
      ];
      if (events.some((e) => e != undefined))
        throw Error(
          "Cannot specify both onEnterLeave and beforeShowPromise/hide/cancel/complete"
        );

      rest.when = {
        hide: onEnterLeave.onLeave,
        cancel: onEnterLeave.onLeave,
        complete: onEnterLeave.onLeave,
        ...(rest.when ?? {}),
      };
      rest.beforeShowPromise = async () => {
        // Avoid flickering by creating a modal during transition from prev.hide to curr.show
        const modal = document.createElement("div");
        modal.classList.add(
          "shepherd-modal-overlay-container",
          "shepherd-modal-is-visible",
          "bg-black"
        );
        document.body.appendChild(modal);

        try {
          await onEnterLeave.onEnter();
          await delayMs(0);
        } finally {
          modal.parentNode?.removeChild(modal);
        }
      };
    }

    tour.addStep({
      attachTo: {
        element,
        on,
      },
      buttons:
        i == 0
          ? other.startButtons
          : i == steps.length - 1
          ? other.endButtons
          : other.midButtons,
      ...rest,
    });
  });

  return tour;
};
